/**
 * Helpers for the Weekplan editor. The legacy DB stores a weekplan as
 * seven 96-character masks (one char per 15-minute slot, "1" = open,
 * "0" = closed). The SPA edits weekplans as a 7-day × 96-slot grid and
 * converts to/from the string masks on load and save.
 *
 * Kept in /lib so the page components have zero pure-data logic inline,
 * which also means these helpers can be unit-tested without a React
 * harness.
 */

/** Canonical day-key order: Monday first, matching the legacy SP. */
export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const dayKeys: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const dayLabels: Record<DayKey, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export const SLOTS_PER_DAY = 96;

/**
 * Turn a 96-character mask into a boolean[96]. Defensively pads or
 * truncates inputs that come in at the wrong length so a stray value
 * from the SP can't crash the editor.
 */
export function maskToSlots(mask: string | null | undefined): boolean[] {
  const padded = padMask(mask);
  const out = new Array<boolean>(SLOTS_PER_DAY);
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    out[i] = padded[i] === "1";
  }
  return out;
}

/** Inverse of <see cref="maskToSlots"/>. */
export function slotsToMask(slots: boolean[]): string {
  const arr = new Array<string>(SLOTS_PER_DAY).fill("0");
  for (let i = 0; i < SLOTS_PER_DAY && i < slots.length; i++) {
    arr[i] = slots[i] ? "1" : "0";
  }
  return arr.join("");
}

function padMask(mask: string | null | undefined): string {
  if (!mask) return "0".repeat(SLOTS_PER_DAY);
  if (mask.length === SLOTS_PER_DAY) return mask;
  if (mask.length > SLOTS_PER_DAY) return mask.slice(0, SLOTS_PER_DAY);
  return mask + "0".repeat(SLOTS_PER_DAY - mask.length);
}

/**
 * Convert a slot index in the range [0, 96) to a "HH:MM" label. Useful
 * both for the hour-line labels on the editor and for tooltips.
 */
export function slotToTime(slot: number): string {
  const clamped = Math.max(0, Math.min(SLOTS_PER_DAY, slot));
  const minutes = clamped * 15;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
